import type { Booking } from '@interfaces/booking.interface';
import type { Guest } from '@interfaces/guest.interface';
import type { BookingMirWorkflow } from '@services/booking-mir-workflow.service';

import { mockDb } from '../mock-db';

/**
 * Guest-flow scenarios for the mock mode: one booking per guest-registration situation, whose
 * workflow follows back-host-bo's `BookingWorkflow` rules (send, Hospedajes' answer, edits after a
 * send, skip and reactivation), so the guests page can be walked through every state.
 * Open `/booking/guest-flow-<scenario>/guests?useMocks=true`.
 */
export type GuestFlowScenario =
  | 'ready'
  | 'pending'
  | 'rejected'
  | 'deadline'
  | 'awaiting'
  | 'unpaid'
  | 'channel'
  | 'skipped'
  | 'family';

export const GUEST_FLOW_PREFIX = 'guest-flow-';

/** Workflow reads a send stays in BOOKING_NOTIFICATION_PENDING before Hospedajes accepts it. */
const READS_UNTIL_ACCEPTED = 2;
const GUEST_COUNT = 2;

interface FlowState {
  general: string;
  payment: string;
  mir: string;
  manual: boolean;
  ota: boolean;
  dataComplete: boolean;
  changesPending: boolean;
  version: number;
  readsUntilAccepted: number | null;
  guests: Guest[];
}

const states = new Map<string, FlowState>();

/** True for a booking id that names a guest-flow scenario. */
export function isGuestFlowBooking(id: string | null | undefined): boolean {
  return !!id && id.startsWith(GUEST_FLOW_PREFIX);
}

/** An adult and the grandchild they travel with (the minor's kinship already chosen). */
function familyGuests(): Guest[] {
  const [adult, other] = completeGuests();
  return [
    { ...adult, name: 'Marcos', birthdate: '2005-05-15' },
    {
      ...other,
      name: 'Lucía',
      birthdate: '2016-03-02',
      docType: undefined,
      docNumber: undefined,
      kinship: 'NI',
      isMainGuest: false,
    },
  ] as Guest[];
}

/** Two adults with every field the guest form requires, the first one the main guest. */
function completeGuests(): Guest[] {
  return ['Claire', 'Louis'].map((name, index) => ({
    id: 9900 + index,
    name,
    firstLastName: 'Martin',
    isMainGuest: index === 0,
    email: `${name.toLowerCase()}@example.com`,
    phone: { prefix: '+33', number: '612345678' },
    docType: 'PAS',
    docNumber: `XK${index}48213`,
    birthdate: '1985-04-12',
    location: {
      countryId: 'FRA',
      address: '12 rue de la Paix',
      zipCode: '75002',
      cityName: 'Paris',
    },
  })) as Guest[];
}

function initialState(scenario: string): FlowState {
  const base: FlowState = {
    general: 'CHECKIN_SCHEDULED',
    payment: 'PAYMENT_PAID',
    mir: 'SEND_GUEST_MIR_DATA_PENDING',
    manual: false,
    ota: false,
    dataComplete: true,
    changesPending: true,
    version: 1,
    readsUntilAccepted: null,
    guests: completeGuests(),
  };
  const incomplete = { dataComplete: false, guests: completeGuests().slice(0, 1) };
  switch (scenario as GuestFlowScenario) {
    case 'pending':
      return { ...base, ...incomplete, mir: 'GUEST_MIR_DATA_PENDING' };
    case 'rejected':
      return { ...base, mir: 'BOOKING_NOTIFICATION_ERROR', changesPending: false };
    case 'deadline':
      return { ...base, ...incomplete, general: 'GUEST_MIR_DATA_TIMEOUT', mir: 'MIR_CANCELLED' };
    case 'awaiting':
      return {
        ...base,
        ...incomplete,
        general: 'ACCEPTANCE_PENDING',
        payment: 'PAYMENT_NOT_STARTED',
        mir: 'MIR_START',
      };
    case 'unpaid':
      return { ...base, payment: 'PAYMENT_PENDING', mir: 'MIR_DATA_READY_PAYMENT_PENDING' };
    case 'channel':
      return {
        ...base,
        ...incomplete,
        payment: 'PAYMENT_EXTERNAL',
        mir: 'GUEST_MIR_DATA_PENDING',
        ota: true,
      };
    case 'family':
      return { ...base, mir: 'SEND_GUEST_MIR_DATA_PENDING', guests: familyGuests() };
    case 'skipped':
      return { ...base, ...incomplete, manual: true, mir: 'MIR_MANUALLY_MANAGED' };
    default:
      return base;
  }
}

function stateOf(id: string): FlowState {
  let state = states.get(id);
  if (!state) {
    state = initialState(id.slice(GUEST_FLOW_PREFIX.length));
    states.set(id, state);
  }
  return state;
}

const CANCELLED = new Set(['GUEST_MIR_DATA_TIMEOUT', 'CANCELLED_BY_GUEST', 'CANCELLED_BY_HOST']);
const PRE_ACCEPTANCE = new Set(['START', 'INSTANT_CHOICE', 'ACCEPTANCE_PENDING']);
const SENT = new Set([
  'BOOKING_NOTIFICATION_PENDING',
  'CHECKIN_PENDING',
  'CHECKIN_NOTIFICATION_PENDING',
  'MIR_COMPLETED',
]);

function mirActive(state: FlowState): boolean {
  return (
    !PRE_ACCEPTANCE.has(state.general) &&
    !CANCELLED.has(state.general) &&
    !state.manual &&
    state.mir !== 'MIR_CANCELLATION_PENDING' &&
    state.mir !== 'MIR_CANCELLED'
  );
}

function paymentBlocks(state: FlowState): boolean {
  return !state.ota && state.payment === 'PAYMENT_PENDING';
}

/** The engine's readiness step: where unsent data waits, by completeness and payment. */
function readiness(state: FlowState): void {
  if (!mirActive(state) || SENT.has(state.mir) || state.mir === 'BOOKING_NOTIFICATION_ERROR') {
    return;
  }
  state.mir = !state.dataComplete
    ? 'GUEST_MIR_DATA_PENDING'
    : paymentBlocks(state)
      ? 'MIR_DATA_READY_PAYMENT_PENDING'
      : 'SEND_GUEST_MIR_DATA_PENDING';
}

/** The booking GET for a scenario (no MIR state of its own, as the BO booking read). */
export function guestFlowBooking(id: string): Booking {
  const state = stateOf(id);
  const base = mockDb.bookings[0];
  return {
    ...base,
    id,
    externalCodeNumber: 990,
    bookingStatus: state.general,
    paymentState: null,
    mirState: null,
    numberOfGuests: GUEST_COUNT,
    stateHistory: [],
    legalStatus: null,
    capabilities: null,
    channel: state.ota ? 'Booking.com' : 'SSR',
    sourceSystem: state.ota ? 'CHANNEX' : null,
    sourceChannel: state.ota ? 'BDC' : null,
    manualMirManaged: state.manual,
    mirReactivationTimeoutRisk: false,
  } as Booking;
}

/** GET /bookings/{id}/workflow for a scenario; a pending send is accepted after a few reads. */
export function guestFlowWorkflow(id: string): BookingMirWorkflow {
  const state = stateOf(id);
  if (state.readsUntilAccepted !== null) {
    state.readsUntilAccepted -= 1;
    if (state.readsUntilAccepted <= 0) {
      state.readsUntilAccepted = null;
      state.mir = 'CHECKIN_PENDING';
      state.version += 1;
    }
  }
  const active = mirActive(state);
  const canSubmit = active && state.dataComplete;
  return {
    manualMirManaged: state.manual,
    generalState: state.general,
    paymentState: state.payment,
    mirState: state.mir,
    canEditGuests: active,
    canSubmitGuestData: canSubmit,
    canSendMir: canSubmit && !paymentBlocks(state),
    canCancelLocally: false,
    canPay: !state.ota && state.payment === 'PAYMENT_PENDING',
    version: state.version,
    guestRevision: state.version,
    state: {
      changesPending: state.changesPending,
      mirStayChangesPending: false,
      mirTimeoutEnabled: true,
      lastMirError: null,
    },
  } as BookingMirWorkflow;
}

/** The scenario's saved guests. */
export function guestFlowGuests(id: string): Guest[] {
  return stateOf(id).guests;
}

/** PUT /guests/{id}: a change marks the data as changed since Hospedajes got it. */
export function guestFlowSaveGuests(id: string, guests: Guest[]): string | null {
  const state = stateOf(id);
  if (!mirActive(state)) {
    return 'MIR_EDIT_NOT_ALLOWED';
  }
  state.guests = guests.map(guest => ({ ...guest }));
  state.dataComplete = guests.length === GUEST_COUNT;
  state.changesPending = true;
  state.version += 1;
  readiness(state);
  return null;
}

/**
 * POST /checkin/{id}: refused where the backend refuses it, a silent no-op for unchanged data
 * Hospedajes holds, else the booking notification goes out.
 */
export function guestFlowSubmit(id: string): string | null {
  const state = stateOf(id);
  if (!mirActive(state)) {
    return 'MIR_EDIT_NOT_ALLOWED';
  }
  if (!state.dataComplete) {
    return 'MIR_GUEST_DATA_INCOMPLETE';
  }
  if (paymentBlocks(state)) {
    return 'MIR_PAYMENT_PENDING';
  }
  if (SENT.has(state.mir) && !state.changesPending) {
    return null;
  }
  state.mir = 'BOOKING_NOTIFICATION_PENDING';
  state.changesPending = false;
  state.version += 1;
  state.readsUntilAccepted = READS_UNTIL_ACCEPTED;
  return null;
}

/** POST workflow/manual-mir: skip (closure completes at once) or reactivate. */
export function guestFlowManualMir(id: string, manual: boolean): BookingMirWorkflow | string {
  const state = stateOf(id);
  if (PRE_ACCEPTANCE.has(state.general) || CANCELLED.has(state.general)) {
    return manual ? 'MIR_MANUAL_NOT_ALLOWED' : 'MIR_REACTIVATION_NOT_ALLOWED';
  }
  state.manual = manual;
  state.version += 1;
  if (manual) {
    state.mir = 'MIR_MANUALLY_MANAGED';
  } else {
    state.mir = 'GUEST_MIR_DATA_PENDING';
    readiness(state);
  }
  return guestFlowWorkflow(id);
}
